<template>
    <div class="main-container">

        <!-- ── Page header ──────────────────────────────────────────── -->
        <div class="invoices-page-header">
            <h1><i class="file dollar icon"></i> Invoices</h1>
        </div>

        <!-- ── Search ───────────────────────────────────────────────── -->
        <div class="invoices-search-row">
            <app-text-input
                class="invoices-search-input"
                icon="search"
                placeholder="Search by number, reference or customer"
                v-model="searchText"
                @keyup="onSearch"
            />
        </div>

        <div class="ui grid invoices-grid-wrapper">
            <div class="sixteen wide column">
                <div class="ui top attached tabular menu overflow-x-hidden">
                    <button type="button" class="item active" data-tab="first" @click="tab = 'all'" @keydown.enter="tab = 'all'" @keydown.space.prevent="tab = 'all'" role="tab" :aria-selected="tab === 'all'">All ({{ counts.all }})</button>
                    <button type="button" class="item" data-tab="second" @click="tab = 'draft'" @keydown.enter="tab = 'draft'" @keydown.space.prevent="tab = 'draft'" role="tab" :aria-selected="tab === 'draft'">Drafts ({{ counts.draft }})</button>
                    <button type="button" class="item" data-tab="third" @click="tab = 'sent'" @keydown.enter="tab = 'sent'" @keydown.space.prevent="tab = 'sent'" role="tab" :aria-selected="tab === 'sent'">Sent ({{ counts.sent }})</button>
                    <button type="button" class="item" data-tab="fourth" @click="tab = 'approved'" @keydown.enter="tab = 'approved'" @keydown.space.prevent="tab = 'approved'" role="tab" :aria-selected="tab === 'approved'">Approved ({{ counts.approved }})</button>
                </div>
            </div>
        </div>

        <!-- All -->
        <div class="ui bottom attached tab segment active" data-tab="first">
            <document-list-view
                :rows="invoiceListRows"
                :loading="loading"
                :headers="headers"
                secondary-date-label="Due"
                @select="preview"
            />
        </div>

        <!-- Draft -->
        <div class="ui bottom attached tab segment" data-tab="second">
            <document-list-view
                :rows="invoiceListRows"
                :loading="loading"
                :headers="headers"
                secondary-date-label="Due"
                @select="preview"
            />
        </div>

        <!-- Sent -->
        <div class="ui bottom attached tab segment" data-tab="third">
            <document-list-view
                :rows="invoiceListRows"
                :loading="loading"
                :headers="headers"
                secondary-date-label="Due"
                @select="preview"
            />
        </div>

        <!-- Approved -->
        <div class="ui bottom attached tab segment" data-tab="fourth">
            <document-list-view
                :rows="invoiceListRows"
                :loading="loading"
                :headers="headers"
                secondary-date-label="Due"
                @select="preview"
            />
        </div>

        <div class="sixteen wide column pagination-controls">
            <app-pagination
              v-if="isEmpty(pagination) === false"
              :page="pagination.page"
              :total-pages="pagination.totalPages"
              @change="fetchInvoices"
            />
        </div>

        <preview v-if="showPreview" :content="selected" @sendOption="openSendOption" @close="closePreview" />
        <option-modal v-if="showOptionModal" title="Send Invoice" :content="selected" @close="showOptionModal = false" @cancel="showOptionModal = false" @email="openEmail" @download="downloadInvoice" />
        <email v-if="showEmail" :content="selected" document-type="invoice" @close="closeEmail" @sent="onEmailSent" />
    </div>
</template>

<script>
import axios from "axios";
import auth from "../../helpers/auth";
import {isEmpty} from "../../helpers/isEmpty";
import Preview from "./Preview";
import {capitalizeFirstLetter} from "../../helpers/capitalizeFirstLetter";
import Email from "../Email";
import OptionModal from "../../components/quote/modals/send/option";
import DocumentListView from "../../components/common/DocumentListView.vue";
import { formatDocumentNumberShort } from "../../helpers/documentNumberDisplay";
import { formatDisplayDate } from "../../helpers/displayDate";
import { quoteDisplayTotal } from "../../helpers/quoteTotals";
import currency from "../../helpers/currency";

export default {
    components: {
        Preview,
        Email,
        OptionModal,
        DocumentListView,
    },

    data: () => ({
        counts: {},
        headers: ['Number', 'Customer', 'Issued', 'Due', 'Status', 'Amount'],
        invoices: [],
        loading: false,
        pagination: {
            page: 1,
            total: 0,
            totalPages: 0,
            previousPage: 0,
            nextPage: 0,
        },
        selected: {},
        showPreview: false,
        showOptionModal: false,
        showEmail: false,
        tab: 'all',
        searchText: '',
        searchTimer: null,
    }),
    computed: {
        invoiceListRows() {
            return (this.invoices || []).map((item) => ({
                id: item.id || item.uuid,
                source: item,
                number: formatDocumentNumberShort(item.number) || '-',
                customerName: this.invoiceCustomerName(item),
                reference: item.reference,
                issuedDate: this.formatMobileDate(item.date),
                secondaryDate: this.formatMobileDate(item.due_date),
                status: this.invoiceStatusLabel(item.status),
                amountText: this.invoiceAmountText(item),
            }));
        }
    },

    methods: {
        capitalizeFirstLetter,
        isEmpty,
        formatMobileDate(value) {
            return formatDisplayDate(value, '-');
        },
        invoiceAmountText(item) {
            return currency(quoteDisplayTotal(item), '$', 2) || '$0.00';
        },
        invoiceStatusLabel(status) {
            return String(status || '').trim().toLowerCase() || '';
        },
        invoiceCustomerName(item) {
            return item && item.contact && item.contact.name ? item.contact.name : '-';
        },

        closePreview(event) {
            if (event && event.refresh) {
                this.fetchInvoices();
            }

            this.showPreview = false;
        },

        closeEmail(event) {
            if (event && event.refresh) {
                this.fetchInvoices();
            }
            this.showEmail = false;
        },

        openSendOption(item) {
            this.selected = item;
            this.showPreview = false;
            this.showOptionModal = true;
        },

        openEmail() {
            this.showOptionModal = false;
            this.showEmail = true;
        },

        onEmailSent() {
            this.fetchInvoices();
            this.closeEmail();
        },

        downloadInvoice() {
            this.showOptionModal = false;
            const item = this.selected;
            if (!item || !item.uuid) return;
            const status = String(item.status || '').toLowerCase();
            // Only mark draft → sent on download; sent/approved stay as-is so customers can get another copy.
            const beforePdf = status === 'draft'
                ? axios.patch(`/invoice/${item.uuid}`, { status: 'sent' }).catch(() => {})
                : Promise.resolve();
            beforePdf.then(() => {
                return axios.get(`/invoice/${item.uuid}/pdf`, { responseType: 'blob' });
            }).then((response) => {
                const file = new Blob([response.data], { type: 'application/pdf' });
                const url = URL.createObjectURL(file);
                const a = document.createElement('a');
                document.body.appendChild(a);
                a.style = 'display: none';
                a.href = url;
                a.download = `${item.number || 'invoice'}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                document.body.removeChild(a);
                this.fetchInvoices();
            }).catch(() => {});
        },

        onSearch() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                this.fetchInvoices(1);
            }, 300);
        },

        fetchInvoices(page = 1) {
            this.loading = true;
            const search = this.searchText ? `&search=${encodeURIComponent(this.searchText)}` : '';
            axios.get(`/invoice?status=${this.tab}&page=${page}${search}`).then((response) => {
                this.invoices = response.data.data.items || [];
                const apiPagination = response.data.data.pagination || { page: 1, total: 0, pages: 0 };
                const currentPage = apiPagination.page || 1;
                const totalPages = apiPagination.totalPages || apiPagination.pages || Math.ceil((apiPagination.total || 0) / (apiPagination.limit || 10)) || 0;
                this.pagination = {
                    page: currentPage,
                    total: apiPagination.total || 0,
                    totalPages,
                    previousPage: currentPage > 1 ? currentPage - 1 : 0,
                    nextPage: currentPage < totalPages ? currentPage + 1 : 0,
                };
                this.counts = Object.assign({ all: 0, draft: 0, sent: 0, approved: 0 }, response.data.data.counts);
            }).catch(() => {
                this.invoices = [];
                this.counts = { all: 0, draft: 0, sent: 0, approved: 0 };
            }).finally(() => {
                this.loading = false;
            });
        },

        preview(item) {
            axios.get(`/invoice/${item.uuid}/items`).then((response) => {
                const payload = response.data.data || {};
                const listContact = (item && item.contact) ? item.contact : {};
                const payloadContact = (payload.contact && typeof payload.contact === 'object') ? payload.contact : {};
                const contact = Object.assign({}, listContact, payloadContact, {
                    email: payloadContact.email || listContact.email || payload.contact_email || ''
                });
                this.selected = Object.assign({}, item, {
                    items: payload.items || [],
                    contact: contact,
                    contact_email: contact.email || '',
                    terms: payload.terms,
                    total: payload.total,
                    sub_total: payload.sub_total,
                    gst_total: payload.gst_total,
                    discount: payload.discount,
                    discount_mode: payload.discount_mode,
                    discount_title: payload.discount_title,
                    discount_description: payload.discount_description,
                    discount_type: payload.discount_type,
                    discount_value: payload.discount_value,
                    discount_amount_applied: payload.discount_amount_applied,
                    company_id: payload.company_id,
                    invoice_signed_by_name: payload.invoice_signed_by_name,
                    invoice_signature_data: payload.invoice_signature_data,
                    invoice_signed_at: payload.invoice_signed_at
                });
                this.showPreview = true;
            });
        },

    },

    created() {
        auth.check(this);
    },

    mounted() {
        /**
         * Initialise Semantic UI tabs
         */
        $(this.$el).find('.ui.menu .item').tab();

        this.fetchInvoices();
    },

    watch: {
        tab: {
            handler() {
                this.fetchInvoices(1);
            },
        }
    }
}
</script>

<style scoped>
.invoices-page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
}

.invoices-page-header h1 {
    margin: 0;
}

.invoices-search-row {
    margin-bottom: 12px;
}

.invoices-search-row .smardie-text-input input {
    height: 44px !important;
    line-height: 44px !important;
}

.invoices-grid-wrapper.ui.grid {
    margin: 0 !important;
}

.invoices-grid-wrapper.ui.grid > .column {
    padding-left: 0 !important;
    padding-right: 0 !important;
}

.ui.bottom.attached.tab.segment {
    padding-left: 0 !important;
    padding-right: 0 !important;
}

@media (max-width: 767px) {
    .invoices-page-header h1 {
        font-size: 1.5em;
    }
}
</style>
